import type { Router } from 'vue-router'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'
import { useWorkspaceStore } from '@/stores/workspace'
import { getToken } from '@/utils/request/token'
import { cancelManager } from '@/utils/request/request-cancel'

NProgress.configure({ showSpinner: false })

const LOGIN_PATH = '/auth'
const FALLBACK_PUBLIC_PATHS: string[] = ['/404']

const isPublicRoute = (path: string, requiresAuth: unknown): boolean =>
  requiresAuth === false || FALLBACK_PUBLIC_PATHS.includes(path)

export function setupGuard(router: Router): void {
  router.beforeEach(async to => {
    NProgress.start()

    document.title = to.meta.title ? `${String(to.meta.title)} - SecKnow` : 'SecKnow'

    const token = getToken()
    const requiresAuth = !isPublicRoute(to.path, to.meta.requiresAuth)
    const workspaceStore = useWorkspaceStore()

    if (!token) {
      if (!requiresAuth) return true
      return { path: LOGIN_PATH, query: { redirect: to.fullPath } }
    }

    // SSO 兑换页需要在已有登录态下继续完成票据兑换。
    if (to.name === 'SsoRedeem') return true

    const needsAuthorization =
      to.path === LOGIN_PATH ||
      requiresAuth ||
      to.meta.consumerOnly === true ||
      to.meta.requiredApplication !== undefined ||
      (to.meta.requiredPermissions?.length ?? 0) > 0

    if (!needsAuthorization) return true

    if (workspaceStore.context === null) {
      await workspaceStore.fetchContext()
    }

    if (to.path === LOGIN_PATH) {
      return workspaceStore.context ? { path: workspaceStore.defaultEntryPath() } : true
    }

    if (!workspaceStore.context) {
      return { path: LOGIN_PATH, query: { redirect: to.fullPath } }
    }

    if (workspaceStore.isExternalWorkspace && (to.meta.consumerOnly === true || to.path === '/')) {
      return { path: '/learning', replace: true }
    }

    // 校本资源仅属于学校租户；其他租户和 C 端手动访问时回到知识星球。
    if (!workspaceStore.isSchoolWorkspace && /^\/learning\/workspace(?:\/|$)/.test(to.path)) {
      return {
        path: to.path.replace(/^\/learning\/workspace/, '/learning/repo'),
        query: to.query,
        hash: to.hash,
        replace: true,
      }
    }

    if (!workspaceStore.isAccessActive && !to.meta.allowBlockedWorkspaceAccess) {
      return { path: '/workspace/status', replace: true }
    }

    if (workspaceStore.isAccessActive && to.meta.allowBlockedWorkspaceAccess) {
      return { path: workspaceStore.defaultEntryPath(), replace: true }
    }

    if (workspaceStore.context.must_change_password && to.path !== '/learning/profile') {
      return {
        path: '/learning/profile',
        query: { action: 'edit-password' },
        replace: true,
      }
    }

    const requiredApplication = to.meta.requiredApplication
    if (requiredApplication && !workspaceStore.hasApplication(requiredApplication)) {
      return { path: workspaceStore.defaultEntryPath(), replace: true }
    }

    if (
      to.meta.homeroomOnly === true &&
      !workspaceStore.hasScope('member.update', 'classroom', 'write')
    ) {
      return { path: workspaceStore.defaultEntryPath(), replace: true }
    }

    const permissions = to.meta.requiredPermissions ?? []
    if (permissions.length > 0) {
      const allowed =
        to.meta.permissionMode === 'all'
          ? workspaceStore.hasAllPermissions(permissions)
          : workspaceStore.hasAnyPermission(permissions)

      if (!allowed) {
        return { path: workspaceStore.defaultEntryPath(), replace: true }
      }
    }

    return true
  })

  router.afterEach(() => {
    NProgress.done()
    cancelManager.cancelAllRequests()
  })

  router.onError(() => {
    NProgress.done()
  })
}
